import React, { useState, useEffect, useRef } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Input } from './Input';
import { XMarkIcon } from '@heroicons/react/24/outline';

export type ModalDialogVariant = 'twoButton' | 'singleButton' | 'input';
export type ModalDialogIcon = 'success' | 'warning' | 'error' | 'info' | 'none';

export interface ModalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  variant?: ModalDialogVariant;
  layout?: 'stacked';
  icon?: ModalDialogIcon | React.ReactNode;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryButtonClick?: () => void;
  onSecondaryButtonClick?: () => void;
  primaryButtonLoading?: boolean;
  secondaryButtonLoading?: boolean;
  primaryButtonColor?: 'primary' | 'secondary' | 'tertiary' | 'white' | 'danger';
  secondaryButtonColor?: 'primary' | 'secondary' | 'tertiary' | 'white' | 'danger';
  inputLabel?: string;
  inputPlaceholder?: string;
  inputValidationText?: string;
  inputValidationRequired?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full';
  hideCloseButton?: boolean;
  calloutText?: React.ReactNode;
  children?: React.ReactNode;
  closeOnEsc?: boolean;
}

export const ModalDialog: React.FC<ModalDialogProps> = ({
  isOpen,
  onClose,
  title,
  description,
  variant = 'twoButton',
  layout: _layout = 'stacked',
  icon = 'info',
  primaryButtonText = 'Confirm',
  secondaryButtonText = 'Cancel',
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  primaryButtonLoading = false,
  secondaryButtonLoading = false,
  primaryButtonColor = 'primary',
  secondaryButtonColor = 'primary',
  inputLabel,
  inputPlaceholder,
  inputValidationText,
  inputValidationRequired = false,
  className,
  size = 'md',
  hideCloseButton = false,
  calloutText,
  children,
  closeOnEsc = true,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(!inputValidationRequired);
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset input value when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setIsConfirmEnabled(!inputValidationRequired);
    }
  }, [isOpen, inputValidationRequired]);

  // Focus management for accessibility
  useEffect(() => {
    if (!isOpen) return;

    // Handle tab navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (e.shiftKey && activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      } else if (!e.shiftKey && activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    // Add event listener
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Get all focusable elements within the modal
  const getFocusableElements = () => {
    if (!modalRef.current) return [];
    
    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const elements = modalRef.current.querySelectorAll(selector);
    
    return Array.from(elements).filter(
      (element) => 
        !element.hasAttribute('disabled') && 
        element.getAttribute('tabindex') !== '-1' &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.getBoundingClientRect().width > 0
    ) as HTMLElement[];
  };

  // Handle input change for input variant
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setIsConfirmEnabled(inputValidationRequired ? value === inputValidationText : value.length > 0);
  };

  // Handle primary button click
  const handlePrimaryButtonClick = () => {
    if (onPrimaryButtonClick) {
      onPrimaryButtonClick();
    }
  };

  // Handle secondary button click or close
  const handleSecondaryButtonClick = () => {
    // Don't allow closing when primary button is loading
    if (primaryButtonLoading) return;
    
    if (onSecondaryButtonClick) {
      onSecondaryButtonClick();
    } else {
      onClose();
    }
  };

  // Handle close button click
  const handleCloseClick = () => {
    // Don't allow closing when primary button is loading
    if (primaryButtonLoading) return;
    
    onClose();
  };

  // Render icon based on type or custom icon
  const renderIcon = () => {
    if (typeof icon === 'object') {
      return icon;
    }

    switch (icon) {
      case 'success':
        return (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
            <svg className="h-6 w-6 text-alerts-success-base" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-alerts-warning-light">
            <svg className="h-6 w-6 text-alerts-warning-base" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-alerts-error-light">
            <svg className="h-6 w-6 text-alerts-error-base" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
      case 'info':
        return (
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
            <svg className="h-6 w-6 text-primary-base" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'none':
      default:
        return null; // Return null for 'none', completely removing the icon
    }
  };

  // Get callout style based on icon type
  const getCalloutStyle = () => {
    if (typeof icon === 'object') {
      return 'bg-primary-50 text-primary-700';
    }

    switch (icon) {
      case 'success':
        return 'bg-green-50 text-green-700';
      case 'warning':
        return 'bg-alerts-error-light text-alerts-error-dark';
      case 'error':
        return 'bg-alerts-error-light text-alerts-error-dark';
      case 'info':
        return 'bg-primary-50 text-primary-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  // Determine if primary button should be disabled
  const isPrimaryButtonDisabled = variant === 'input' ? !isConfirmEnabled : false;

  // Determine if ESC key should close the modal - disable it when loading
  const shouldCloseOnEsc = closeOnEsc && !primaryButtonLoading;

  return (
    <Modal
      isOpen={isOpen}
      onClose={primaryButtonLoading ? () => {} : onClose}
      className={className}
      size={size}
      closeOnEsc={shouldCloseOnEsc}
    >
      <div className="relative p-6" ref={modalRef}>
        {!hideCloseButton && (
          <button
            type="button"
            className={`absolute right-4 top-4 text-gray-400 ${primaryButtonLoading ? 'opacity-50 cursor-not-allowed' : 'hover:text-gray-500'} focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 rounded-lg p-1`}
            onClick={handleCloseClick}
            disabled={primaryButtonLoading}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        )}

        <div className="mb-6">
          {icon !== 'none' && (
            <div className="mb-4 flex justify-center">
              {renderIcon()}
            </div>
          )}
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-900">{title}</h3>
            {description && <p className="mt-2 text-gray-500">{description}</p>}
          </div>

          {calloutText && (
            <div className={`mt-4 p-4 rounded-md ${getCalloutStyle()} text-left`}>
              {calloutText}
            </div>
          )}

          {children && <div className="mt-4">{children}</div>}
        </div>

        {variant === 'input' && (
          <div className="mb-6">
            <Input
              label={inputLabel}
              value={inputValue}
              onChange={handleInputChange}
              placeholder={inputPlaceholder}
              helperText={inputValidationRequired ? `Please type "${inputValidationText}" to confirm` : undefined}
              isRequired={true}
              disabled={primaryButtonLoading}
            />
          </div>
        )}

        {/* Buttons section */}
        <div className="flex flex-col space-y-4">
          {(variant === 'twoButton' || variant === 'input') && (
            <>
              <Button
                color={primaryButtonColor}
                variant="filled"
                className="w-full"
                onClick={handlePrimaryButtonClick}
                loading={primaryButtonLoading}
                disabled={isPrimaryButtonDisabled}
              >
                {primaryButtonText}
              </Button>
              <Button
                color={secondaryButtonColor}
                variant="outline"
                className="w-full"
                onClick={handleSecondaryButtonClick}
                loading={secondaryButtonLoading}
                disabled={primaryButtonLoading}
              >
                {secondaryButtonText}
              </Button>
            </>
          )}

          {variant === 'singleButton' && (
            <Button
              color={primaryButtonColor}
              variant="filled"
              className="w-full"
              onClick={handlePrimaryButtonClick}
              loading={primaryButtonLoading}
            >
              {primaryButtonText}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}; 